import * as React from 'react';
import { Chip, Stack, Tooltip, Typography } from '@mui/material';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { LastSubscriptionDto } from '../models/dtos/AccountMainInfoDto';

/** A player's subscription tier (core qbet_user_info.subscriptionTier, betup-server-api docs/subscriptions.md §1). */
export type SubscriptionTier = 'FREE' | 'PLUS' | 'MAX';

const TIER_VIEW: Record<'PLUS' | 'MAX', { label: string; color: 'primary' | 'warning'; hint: string }> = {
    PLUS: { label: 'Plus', color: 'primary', hint: 'BETUP Plus — the Plus badge in the app' },
    MAX: { label: 'Max', color: 'warning', hint: 'BETUP Max — the Max badge in the app' },
};

/** The tier as a chip: Plus / Max with the plan colours, a dash (or "Free" when [showFree]) without one. */
export default function SubscriptionTierChip({ tier, showFree = false, size = 'small' }: {
    tier?: string | null;
    showFree?: boolean;
    size?: 'small' | 'medium';
}) {
    const view = tier === 'PLUS' || tier === 'MAX' ? TIER_VIEW[tier] : null;
    if (view == null) {
        return showFree
            ? <Chip size={size} variant="outlined" label="Free" />
            : <Typography variant="body2" color="text.secondary">—</Typography>;
    }
    return (
        <Tooltip title={view.hint}>
            <Chip size={size} color={view.color} icon={<WorkspacePremiumIcon />} label={view.label} sx={{ fontWeight: 700 }} />
        </Tooltip>
    );
}

/**
 * The user page's tier: with a live plan subscription the tier and a green Active badge; when the last one ended, that
 * subscription's tier (not FREE) and an orange Expired badge; without any plan subscription the plain tier (a legacy
 * VIP's PLUS included).
 */
export function UserTierStatus({ tier, last }: { tier?: string | null; last?: LastSubscriptionDto | null }) {
    if (last == null) return <SubscriptionTierChip tier={tier} showFree />;
    const until = last.endsAt != null ? new Date(last.endsAt).toLocaleDateString() : null;
    return (
        <Stack direction="row" gap={0.5} alignItems="center">
            <SubscriptionTierChip tier={last.active ? tier : last.tier} showFree />
            <Tooltip title={`Subscription #${last.subscriptionId} · ${last.status ?? '—'}${until ? ` · ${last.active ? 'until' : 'ended'} ${until}` : ''}`}>
                <Chip size="small" variant="outlined" color={last.active ? 'success' : 'warning'} label={last.active ? 'Active' : 'Expired'} />
            </Tooltip>
        </Stack>
    );
}
